import { useTranslation } from "react-i18next";

/**
 * The build under test.
 *
 * TWO FIELDS, AND THE SHORT ONE IS NOT THE ONE TO TRUST. `build` is what the
 * product says about itself and `build_ref` is what it can be rebuilt from,
 * and for SNCF the first is actively misleading: "1.7.13+dis.1.7.104.0" moves
 * whenever anything merges to device-interface, with no change to the image at
 * all - 2026-08-18 saw dis1.6.84 become dis1.6.88 against an unchanged
 * be574fcc. This repo's own instruction is to compare the last member, not the
 * version number.
 *
 * So the ref is on screen wherever the label alone could mislead - never only
 * behind a hover, the list is read on a phone where `title` does not exist -
 * and nothing anywhere offers to sort by build: the ref is a sha and the SNCF
 * label sorts beautifully and falsely.
 *
 * WHERE IT COULD MISLEAD (2026-09-16). A commit on every row of every list is
 * the developer output the clear-screens rules take off the screen: people read
 * "7.2.72". The run page keeps the ref (under Details); a LIST shows it for the
 * runs `refNeeded` picks - one label under two refs, or one ref under two
 * labels, which is exactly the dis1.6.84 / dis1.6.88 / be574fcc case - and the
 * Now card, one run per bench, shows the label alone.
 */
const needed = new WeakMap<object, Set<number>>();

/** The runs of `runs` whose build label cannot be trusted alone within `runs`. */
export function refNeeded(
  runs: { id: number; build?: string; build_ref?: string; build_recorded?: boolean }[],
): Set<number> {
  const hit = needed.get(runs);
  if (hit) return hit;
  const refsOf = new Map<string, Set<string>>();
  const labelsOf = new Map<string, Set<string>>();
  for (const r of runs) {
    if (!r.build_recorded || !r.build_ref) continue;
    const label = r.build ?? "";
    if (!refsOf.has(label)) refsOf.set(label, new Set());
    refsOf.get(label)!.add(r.build_ref);
    if (!labelsOf.has(r.build_ref)) labelsOf.set(r.build_ref, new Set());
    labelsOf.get(r.build_ref)!.add(label);
  }
  const out = new Set(
    runs
      .filter((r) => r.build_recorded && r.build_ref && (
        !r.build
        || (refsOf.get(r.build)?.size ?? 0) > 1
        || (labelsOf.get(r.build_ref)?.size ?? 0) > 1))
      .map((r) => r.id),
  );
  needed.set(runs, out);
  return out;
}
export function shortRef(ref: string): string {
  // A sha, shortened. Anything else - a tag like `displayos-rig:d2385105`, a
  // `pdc@sha` - is left whole: truncating a name is how you get two builds
  // that look identical.
  return /^[0-9a-f]{12,}$/i.test(ref) ? ref.slice(0, 8) : ref;
}

export default function Build({
  build,
  ref_,
  recorded,
  variant,
  variantLabel,
  showRef = true,
}: {
  build?: string;
  ref_?: string;
  recorded?: boolean;
  variant?: string;
  variantLabel?: string;
  /** False where the label alone is enough to read (see refNeeded). */
  showRef?: boolean;
}) {
  const { t } = useTranslation();
  // Absent, not empty. A run from before the harness reported this has no
  // build; saying so on the DETAIL page is worth a chip, because a reader
  // deciding what a result means needs to know the question is unanswered.
  if (!recorded) {
    return (
      <span
        title={t("build.notRecordedTitle")}
        className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400"
      >
        {t("build.notRecorded")}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-baseline gap-1.5">
      {build && (
        <span className="font-mono text-xs" title={!showRef && ref_ ? ref_ : undefined}>{build}</span>
      )}
      {ref_ && (showRef || !build) && (
        <span
          title={ref_}
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        >
          {shortRef(ref_)}
        </span>
      )}
      {variant && (
        <span
          title={variantLabel || variant}
          className="rounded bg-sky-50 px-1.5 py-0.5 text-[11px] text-sky-900 dark:bg-sky-950 dark:text-sky-200"
        >
          {variantLabel || variant}
        </span>
      )}
    </span>
  );
}
